import Big from 'big.js';
import { Map as IMap, List as IList, fromJS } from 'immutable';
import { createSelector } from 'reselect';
import { getIntlCurrencyCode, messages } from '../intl/intlSelectors';
import { getInCatalog } from './catalogUtils';
import firstDataBrands from './data/firstDataBrands';
import excludedBillingCountriesByBrand from './data/excludedBillingCountriesByBrand';
import excludedShippingCountriesByBrand from './data/excludedShippingCountriesByBrand';
import { OPTIONAL } from '../address/addressModule';
import { MAX_PLASTIC_CARD_QUANTITY } from '../constants/constants';
import { getFaceplateGroups } from '../landingPage/landingPageSelectors';
export const getBrand = state => state.get('brand', IMap());

export const getSupportedLocales = createSelector(
    getBrand,
    brand => brand.get('supportedLocales', IList())
);

export const getBrandCode = createSelector(
    getBrand,
    brand => brand.get('brandCode')
);

export const getBrandElectronicCurrencyList = createSelector(
    getBrand,
    brand => brand.get('electronicCurrencyList', IList())
);

export const getBrandPlasticCurrencyList = createSelector(
    getBrand,
    brand => brand.get('plasticCurrencyList', IList())
);

export const getRequireRecipientEmailForPhysicalGift = createSelector(
    getBrand,
    brand => brand.get('requireRecipientEmailForPhysicalGift', OPTIONAL)
);

export const getBrandName = createSelector(
    getBrand,
    brand => brand.get('brandName')
);

export const getgdprValue = createSelector(
    getBrand,
    brand => brand.get('gdpr', IMap())
);

export const getgdprTermsAcknowledgementEnabled = createSelector(
    getgdprValue,
    gdpr => gdpr.get('gdprTermsAcknowledgementEnabled')
);

export const getNotifyOffersConfig = createSelector(
    getBrand,
    brand => brand.get('notifyOffers', IMap())
);

export const getSenderOptInEnabled = createSelector(
    getNotifyOffersConfig,
    offerConfig => offerConfig.get('senderEnabled', false)
);

export const getDefaultOptInValue = createSelector(
    getNotifyOffersConfig,
    offerConfig => offerConfig.get('senderDefaultValue', false)
);


export const getGdprTermsAcknowledgementFormat = createSelector(
    [getgdprValue, getgdprTermsAcknowledgementEnabled],
    (gdprConfig, gdprEnabled) => (gdprEnabled ? gdprConfig.get('gdprTermsAcknowledgementFormat', 'IMPLICIT') : null)
);

export const getBrandCatalogs = state => state.getIn(['brand', 'catalogs'], IMap());

export const getBannerData = state => state.getIn(['brand', 'bannerData'], IList());

export const getReadyToLoadPDP = createSelector(
    getBrandCatalogs,
    getFaceplateGroups,
    (catalogs, faceplatesData) => !!((catalogs && catalogs.size > 0 && faceplatesData && faceplatesData.size > 0))
);

export const getCatalogForCurrency = (catalogs, currency) => (
    catalogs.get(currency) || IMap()
);

export const getEgcDefaultCurrency = createSelector(
    getBrandElectronicCurrencyList,
    defaultCurrency => defaultCurrency.get(0)
);

export const getEgcRecommendedDenoms = createSelector(
    getBrandCatalogs,
    getIntlCurrencyCode,
    (catalogs, currency) => {
        const egcRecommendedDenoms = getInCatalog(
            getCatalogForCurrency(catalogs, currency),
            ['egcRecommendedDenoms'],
            IList()
        );
        return egcRecommendedDenoms.map(value => new Big(parseFloat(value).toFixed(2)));
    }
);

export const getPlasticRecommendedDenoms = createSelector(
    getBrandCatalogs,
    getIntlCurrencyCode,
    (catalogs, currency) => {
        const plasticRecommendedDenoms = getInCatalog(
            getCatalogForCurrency(catalogs, currency),
            ['plasticRecommendedDenoms'],
            IList()
        );
        return plasticRecommendedDenoms.map(value => new Big(parseFloat(value).toFixed(2)));
    }
);

export const getEgcDefaultAmount = createSelector(
    getBrandCatalogs,
    getIntlCurrencyCode,
    (catalogs, currency) => {
        const egcDefaultAmount = getInCatalog(
            getCatalogForCurrency(catalogs, currency),
            ['egcDefaultAmount'],
            IList()
        );
        return egcDefaultAmount;
    }
);


export const getPlasticDefaultAmount = createSelector(
    getBrandCatalogs,
    getIntlCurrencyCode,
    (catalogs, currency) => {
        const plasticDefaultAmount = getInCatalog(
            getCatalogForCurrency(catalogs, currency),
            ['plasticDefaultAmount'],
            IList()
        );
        return plasticDefaultAmount;
    }
);

export const getEgcRangedCatalog = createSelector(
    getBrandCatalogs,
    getIntlCurrencyCode,
    (catalogs, currency) => {
        const egcRangedCatalog = getInCatalog(
            getCatalogForCurrency(catalogs, currency),
            ['egcRangedCatalog'],
            IList()
        );
        return egcRangedCatalog;
    }
);

export const getPlasticRangedCatalog = createSelector(
    getBrandCatalogs,
    getIntlCurrencyCode,
    (catalogs, currency) => {
        const plasticRangedCatalog = getInCatalog(
            getCatalogForCurrency(catalogs, currency),
            ['plasticRangedCatalog'],
            IList()
        );
        return plasticRangedCatalog;
    }
);

export const getEgcRangedCatalogForCurrency = currency => createSelector(
    getBrandCatalogs,
    (catalogs) => {
        const egcRangedCatalog = getInCatalog(
            getCatalogForCurrency(catalogs, currency),
            ['egcRangedCatalog'],
            IList()
        );
        return egcRangedCatalog;
    }
);

export const getPlasticRangedCatalogForCurrency = currency => createSelector(
    getBrandCatalogs,
    (catalogs) => {
        const egcRangedCatalog = getInCatalog(
            getCatalogForCurrency(catalogs, currency),
            ['plasticRangedCatalog'],
            IList()
        );
        return egcRangedCatalog;
    }
);

export const getBrandDefaultLocale = createSelector(
    getBrand,
    brand => brand.get('defaultLocaleCode')
);

export const getProfanityDetectionEnabled = createSelector(
    getBrand,
    brand => brand.get('isProfanityDetectionEnabled', true)
);

export const getfeatureValue = createSelector(
    getBrand,
    brand => brand.get('features', IMap())
);

export const getShareViaLinkEnabled = createSelector(
    getfeatureValue,
    features => features.get('IsShareViaLinkDeliveryOptionEnabled')
);

export const getShareViaLinkDefaultOptionEnabled = createSelector(
    getfeatureValue,
    features => features.get('isShareViaLinkDefaultDeliveryOption')
);

export const getLoyaltyProgramConfig = createSelector(
    getBrand,
    brand => brand.get('loyaltyProgram', IMap())
);

export const getLoyaltyProgramEnabled = createSelector(
    getLoyaltyProgramConfig,
    loyaltyProgramConfig => loyaltyProgramConfig.get('loyaltyProgramEnabled', false)
);

export const getLoyaltyProgramValidator = createSelector(
    getLoyaltyProgramConfig,
    (loyaltyProgramConfig) => {
        let validatorRegexString = loyaltyProgramConfig.get('loyaltyNumberValidator', '');
        // If the regex configuration was malformed, we don't want the user unable to input loyalty
        try {
            RegExp(validatorRegexString);
        } catch (error) {
            validatorRegexString = '';
        }
        return validatorRegexString;
    }
);

/**
 * getIsBrandFirstData - currently a hardcoded list of brands
 * @return {boolean} True if the brand, based on its code, is a
 *                   firstDataBrand, or false.
 */

export const getIsBrandFirstData = createSelector(
    getBrandCode,
    brandCode => firstDataBrands.contains(brandCode)
);

/**
 * Get cardholder agreement configuration
 * @param {Immutable.Map} state
 * @returns {Immutable.Map} cardholder subsection of brand preferences
 */
export const getCardholderAgreementConfig = createSelector(
    getBrand,
    brand => brand.get('cardholderAgreement', IMap())
);

export const getCardholderAgreementEnabled = createSelector(
    getCardholderAgreementConfig,
    cardHolderConfig => cardHolderConfig.get('cardholderAgreementEnabled', false)
);

export const getGreetingsPricePerCurrency = createSelector(
    getBrand,
    brand => brand.get('greetingsPricePerCurrency', IMap())
);

export const getGreetingsPrice = createSelector(
    getGreetingsPricePerCurrency,
    greetingsPricePerCurrency => greetingsPricePerCurrency.get('USD', 0)
);

export const getCardholderAgreementFormat = createSelector(
    [getCardholderAgreementConfig, getCardholderAgreementEnabled],
    (cardHolderConfig, cardHolderEnabled) => (cardHolderEnabled ? cardHolderConfig.get('cardholderAgreementFormat', 'IMPLICIT') : null)
);


export const getCartMaxLineItems = createSelector(
    getBrand,
    brand => brand.get('cartMaxLineItems')
);

export const getProgramState = state => state.get('programs', IMap());


export const getActiveProgramCode = createSelector(
    getProgramState,
    programState => programState.get('activeProgramCode', null)
);

export const programIsActive = createSelector(
    getActiveProgramCode,
    programCode => programCode !== null
);

export const getProgramConfigs = createSelector(
    getProgramState,
    programState => programState.get('programConfig', IMap())
);

export const getProgramConfig = createSelector(
    getProgramConfigs,
    programConfigs => programCode => programConfigs.get(programCode, IMap())
);

export const getActiveProgramConfig = createSelector(
    getProgramConfig,
    getActiveProgramCode,
    (programConfig, activeProgramCode) => programConfig(activeProgramCode)
);

export const getActiveProgramCatalogs = createSelector(
    getActiveProgramConfig,
    programConfig => programConfig.get('catalogs', IMap())
);

export const getCatalogs = createSelector(
    getBrandCatalogs,
    programIsActive,
    getActiveProgramCatalogs,
    (brandCatalogs, useProgram, programCatalogs) => (useProgram ? programCatalogs : brandCatalogs)
);

export const getMaxTransactionAmount = createSelector(
    getCatalogs,
    getIntlCurrencyCode,
    (catalogs, currency) => new Big(getInCatalog(
        getCatalogForCurrency(catalogs, currency),
        ['maxTransactionAmount'],
        '500.00'
    ))
);

export const getUYOEgcPhotoSupported = createSelector(
    getfeatureValue,
    features => features.get('uploadEgcPhotoEnabled')
);

export const getUYOEgcVideoSupported = createSelector(
    getfeatureValue,
    features => features.get('uploadEgcVideoEnabled')
);

export const getUYOPlasticPhotoSupported = createSelector(
    getfeatureValue,
    features => features.get('uploadPlasticPhotoEnabled')
);

export const getUYOEgcPhotoBrandOverlayFaceplate = createSelector(
    getfeatureValue,
    features => features.get('uploadEgcPhotoBrandOverlayFaceplate', IMap())
);


export const getUYOPlasticPhotoBrandOverlayFaceplate = createSelector(
    getfeatureValue,
    features => features.get('uploadPlasticPhotoBrandOverlayFaceplate', IMap())
);


export const getConfiguredBillingCountries = createSelector(
    getBrand,
    brand => brand.get('allowedBillingCountries', IList())
);

export const getExcludedBillingCountries = createSelector(
    getBrandCode,
    brandCode => excludedBillingCountriesByBrand.get(brandCode, IList())
);

export const getConfiguredShippingCountries = createSelector(
    getBrand,
    brand => brand.get('shippingCurrenciesByCountry', IMap())
);

export const getExcludedShippingCountries = createSelector(
    getBrandCode,
    brandCode => excludedShippingCountriesByBrand.get(brandCode, IList())
);

export const getBrandExcludedBillingStates = createSelector(
    getBrand,
    brand => brand.get('excludedBillingStates', '')
);

/**
 * Get max number of plastic cards allowed in the cart
 * @param {Immutable.Map} state
 * @returns {number}
 */
export const getCartMaxNumPlasticCards = createSelector(
    getBrand,
    brand => brand.get('cartMaxNumPlasticCards') ?? MAX_PLASTIC_CARD_QUANTITY
);

export const getAllowedShippingCountries = createSelector(
    [getConfiguredShippingCountries, getExcludedShippingCountries, getIntlCurrencyCode],
    (allShippingCountries, excludedShippingCountries, currencyCode) => allShippingCountries
        .filter(
            (currencies, shippingCountry) => (
                !excludedShippingCountries.includes(shippingCountry) && currencies.includes(currencyCode)
            )
        )
        .keySeq()
        .toList()
);

export const getAllowsAllBillingCountries = createSelector(
    getBrand,
    brand => brand.get('allowAllInternationalBillingCountries')
);

export const getAllowedBillingCountries = createSelector(
    getConfiguredBillingCountries,
    getExcludedBillingCountries,
    (allBillingCountries, excludedBillingCountries) => allBillingCountries.filter(
        shippingCountry => !excludedBillingCountries.includes(shippingCountry)
    )
);

export const getPromoBoxEnabled = createSelector(
    getfeatureValue,
    features => features.get('promoBoxEnabled')
);

export const getBrandSupportedCardTypes = createSelector(
    getBrand,
    brand => fromJS({
        supportsDigitalCards: brand.get('supportsDigitalCards'),
        supportsPlasticCards: brand.get('supportsPlasticCards')
    })
);

export const hasClassicCatalogs = createSelector(
    getBrandCatalogs,
    brandCatalogs => !brandCatalogs.isEmpty()
);

export const getBrandPaymentLegalDisclaimer = createSelector(
    messages,
    msgs => msgs.get('payment.brand.legalDisclaimer', '')
);


/**
 * Get the fixed plastic catalog.
 * @param {Immutable.Map} state The current state.
 * @returns {Immutable.List} Returns the fixed plastic catalog as an IList.
 */
export const getFixedPlasticCatalog = createSelector(
    getCatalogs,
    getIntlCurrencyCode,
    (catalogs, currency) => {
        const fixedPlasticCatalog = getInCatalog(
            getCatalogForCurrency(catalogs, currency),
            ['plasticFixedCatalog'],
            IList()
        );
        return fixedPlasticCatalog.map(value => new Big(parseFloat(value).toFixed(2)));
    }
);

export const getIsFixedPlasticCatalogPresent = createSelector(
    getFixedPlasticCatalog,
    catalog => catalog.size > 0
);

export const getFixedEgcCatalog = createSelector(
    getCatalogs,
    getIntlCurrencyCode,
    (catalogs, currency) => {
        const fixedEgcCatalog = getInCatalog(
            getCatalogForCurrency(catalogs, currency),
            ['egcFixedCatalog'],
            IList()
        );
        return fixedEgcCatalog.map(value => new Big(parseFloat(value).toFixed(2)));
    }
);

export const getIsFixedEgcCatalogPresent = createSelector(
    getFixedEgcCatalog,
    catalog => catalog.size > 0
);
export const getKlarnaEnabled = createSelector(
    getBrand,
    brand => brand.get('klarnaEnabledCurrencies', IList())
);

export const getFixedEgcCatalogForCurrency = currency => createSelector(
    getBrandCatalogs,
    (catalogs) => {
        const egcFixedCatalog = getInCatalog(
            getCatalogForCurrency(catalogs, currency),
            ['egcFixedCatalog'],
            IList()
        );
        return egcFixedCatalog;
    }
);

export const getIsFixedEgcCatalogPresentForCurrency = currency => createSelector(
    getFixedEgcCatalogForCurrency(currency),
    catalog => catalog.size > 0
);

export const getFixedPlasticCatalogForCurrency = currency => createSelector(
    getBrandCatalogs,
    (catalogs) => {
        const plasticFixedCatalog = getInCatalog(
            getCatalogForCurrency(catalogs, currency),
            ['plasticFixedCatalog'],
            IList()
        );
        return plasticFixedCatalog;
    }
);

export const getIsFixedPlasticCatalogPresentForCurrency = currency => createSelector(
    getFixedPlasticCatalogForCurrency(currency),
    catalog => catalog.size > 0
);


export const getIsCPFEnabledForSelectedCurrency = createSelector(
    getCatalogs,
    getIntlCurrencyCode,
    (catalogs, selectedCurrencyCode) => {
        const catalogResult = catalogs.filter(catalog => catalog.get('cpfFlag') === true);
        const cpfEnabledCurrencyList = Object.keys(catalogResult.toJS());
        return cpfEnabledCurrencyList.includes(selectedCurrencyCode);
    }
);

export const isAccessoriesEnabled = createSelector(
    getfeatureValue,
    features => features.get('cardAccessoriesEnabled', false)
);

export const getAccessories = createSelector(
    getBrand,
    brand => brand.get('cardAccessories', IMap())
);

export const getDigitalAccessoriesList = createSelector(
    getAccessories,
    cardAccessories => cardAccessories.get('digitalAccessories', IList())
);

export const getDigitalAccessoryByOrdinal = createSelector(
    getDigitalAccessoriesList,
    digitalAccessories => (
        ordinal => digitalAccessories.find(accessory => accessory.get('egcOrdinal') === ordinal)
    )
);

export const getPhysicalAccessoriesList = createSelector(
    getAccessories,
    cardAccessories => cardAccessories.get('physicalAccessories', IList())
);

export const getPhysicalAccessoryByOrdinal = createSelector(
    getPhysicalAccessoriesList,
    physicalAccessories => (
        ordinal => physicalAccessories.find(accessory => accessory.get('plasticOrdinal') === ordinal)
    )
);
